import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchData, updateData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Button from '@mui/material/Button';
import CopyrightIcon from '@mui/icons-material/Copyright';
import { CircularProgress, Fade, Tooltip, Chip, LinearProgress, Select, MenuItem, FormControl, Collapse, IconButton } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SettingsIcon from '@mui/icons-material/Settings';
import BranchCreateModal from './BranchCreateModal';
import BranchEditModal from './BranchEditModal';

// Helper function to display field values with placeholder for null/empty
const displayValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return 'Not specified';
    }
    return value;
};

// BranchDetailCard component for displaying expanded branch details
const BranchDetailCard = ({ branch }) => {
    return (
        <div style={{
            background: 'linear-gradient(145deg, #1e3a8a 0%, #1e40af 100%)',
            border: '1px solid #3b82f6',
            borderTop: 'none',
            borderRadius: '0 0 8px 8px',
            padding: '16px',
            margin: '0 8px 8px 8px',
            boxShadow: '0 4px 12px rgba(30, 58, 138, 0.3)',
            borderLeft: '3px solid #fbbf24',
        }}>
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '16px'
            }}>
                {/* Contact Information Section */}
                <div style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '8px',
                    padding: '12px',
                    border: '1px solid #3b82f6'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '12px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #fbbf24'
                    }}>
                        <PhoneIcon style={{ fontSize: '16px', color: '#fbbf24' }} />
                        <span style={{
                            fontSize: '12px',
                            fontWeight: '700',
                            color: '#1e3a8a',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>Contact Information</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div>
                            <span style={{ fontSize: '10px', color: '#1e40af', fontWeight: '600', textTransform: 'uppercase' }}>Primary Contact</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#1e293b' }}>{displayValue(branch.ContactNumber1)}</p>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', color: '#1e40af', fontWeight: '600', textTransform: 'uppercase' }}>Secondary Contact</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#1e293b' }}>{displayValue(branch.ContactNumber2)}</p>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', color: '#1e40af', fontWeight: '600', textTransform: 'uppercase' }}>Alternate Contact</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#1e293b' }}>{displayValue(branch.ContactNumber3)}</p>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', color: '#1e40af', fontWeight: '600', textTransform: 'uppercase' }}>Email</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#1e293b' }}>{displayValue(branch.Email)}</p>
                        </div>
                    </div>
                </div>

                {/* Address Details Section */}
                <div style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '8px',
                    padding: '12px',
                    border: '1px solid #3b82f6'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '12px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #fbbf24'
                    }}>
                        <LocationOnIcon style={{ fontSize: '16px', color: '#fbbf24' }} />
                        <span style={{
                            fontSize: '12px',
                            fontWeight: '700',
                            color: '#1e3a8a',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>Address Details</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div>
                            <span style={{ fontSize: '10px', color: '#1e40af', fontWeight: '600', textTransform: 'uppercase' }}>Address Line 1</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#1e293b' }}>{displayValue(branch.AddressLine1)}</p>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', color: '#1e40af', fontWeight: '600', textTransform: 'uppercase' }}>Address Line 2</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#1e293b' }}>{displayValue(branch.AddressLine2)}</p>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', color: '#1e40af', fontWeight: '600', textTransform: 'uppercase' }}>Location</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#1e293b' }}>{displayValue(branch.Location)}</p>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', color: '#1e40af', fontWeight: '600', textTransform: 'uppercase' }}>Pin Code</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#1e293b' }}>{displayValue(branch.PinCode)}</p>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', color: '#1e40af', fontWeight: '600', textTransform: 'uppercase' }}>State Code ID</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#1e293b' }}>{displayValue(branch.StateCodeId)}</p>
                        </div>
                    </div>
                </div>

                {/* Tax Information Section */}
                <div style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '8px',
                    padding: '12px',
                    border: '1px solid #3b82f6'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '12px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #fbbf24'
                    }}>
                        <ReceiptIcon style={{ fontSize: '16px', color: '#fbbf24' }} />
                        <span style={{
                            fontSize: '12px',
                            fontWeight: '700',
                            color: '#1e3a8a',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>Tax Information</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div>
                            <span style={{ fontSize: '10px', color: '#1e40af', fontWeight: '600', textTransform: 'uppercase' }}>VAT Number</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#1e293b', fontFamily: 'monospace' }}>{displayValue(branch.GSTNumber)}</p>
                        </div>
                        {/* <div>
                            <span style={{ fontSize: '10px', color: '#1e40af', fontWeight: '600', textTransform: 'uppercase' }}>PAN Number</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#1e293b', fontFamily: 'monospace' }}>{displayValue(branch.PanNumber)}</p>
                        </div> */}
                    </div>
                </div>

                {/* Configuration Settings Section */}
                <div style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '8px',
                    padding: '12px',
                    border: '1px solid #3b82f6'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '12px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #fbbf24'
                    }}>
                        <SettingsIcon style={{ fontSize: '16px', color: '#fbbf24' }} />
                        <span style={{
                            fontSize: '12px',
                            fontWeight: '700',
                            color: '#1e3a8a',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>Configuration Settings</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div>
                            <span style={{ fontSize: '10px', color: '#1e40af', fontWeight: '600', textTransform: 'uppercase' }}>E-Invoicing</span>
                            <div style={{ marginTop: '4px' }}>
                                <Chip
                                    label={branch.IsEInvoicingEnabled ? 'Enabled' : 'Disabled'}
                                    size="small"
                                    style={{
                                        height: '20px',
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        background: branch.IsEInvoicingEnabled
                                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                        color: 'white'
                                    }}
                                />
                            </div>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', color: '#1e40af', fontWeight: '600', textTransform: 'uppercase' }}>E-Waybill</span>
                            <div style={{ marginTop: '4px' }}>
                                <Chip
                                    label={branch.IsEWaybillEnabled ? 'Enabled' : 'Disabled'}
                                    size="small"
                                    style={{
                                        height: '20px',
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        background: branch.IsEWaybillEnabled
                                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                        color: 'white'
                                    }}
                                />
                            </div>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', color: '#1e40af', fontWeight: '600', textTransform: 'uppercase' }}>B2B Prefix</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#1e293b', fontFamily: 'monospace' }}>{displayValue(branch.BToBPrefix)}</p>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', color: '#1e40af', fontWeight: '600', textTransform: 'uppercase' }}>B2C Prefix</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#1e293b', fontFamily: 'monospace' }}>{displayValue(branch.BToCPrefix)}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CompanyEdit = ({ match }) => {
    // Session validation
    useSessionValidation();
    
    const { isAuthenticated, companyId, logout, token, userId } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [formProgress, setFormProgress] = useState(0);
    const [openBranchModal, setOpenBranchModal] = useState(false);
    const [openBranchEditModal, setOpenBranchEditModal] = useState(false);
    const [selectedBranchForEdit, setSelectedBranchForEdit] = useState(null);
    const [isSavingBranch, setIsSavingBranch] = useState(false);
    const [expandedBranchId, setExpandedBranchId] = useState(null);

    // Toggle branch expansion - collapse if same branch clicked, expand new one otherwise
    const handleBranchRowClick = (branchId) => {
        setExpandedBranchId(prevId => prevId === branchId ? null : branchId);
    };

    useEffect(() => {
        setMounted(true);
        // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }

    }, [isAuthenticated, companyId]);

    const [formData, setFormData] = useState([]);
    const [branches, setBranches] = useState([
        // { id: 1, code: 'HQ01', name: 'Head Office', contact: '5551234567', status: 'Active' },
        // { id: 2, code: 'BR02', name: 'North Branch', contact: '5559871234', status: 'Active' },
        // { id: 3, code: 'BR03', name: 'South Branch', contact: '5555647890', status: 'Active' }
    ]);

    useEffect(() => {
        fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token)//, token
            .then((response) => {
                if (response.responseCode == '1111') {
                    setFormData(response.response_data);
                    setTimeout(() => calculateProgress(), 200);
                }
            })
            .catch((error) => {
                console.error('Error fetching Company Details:', error);
            });
    }, []);

    // branch list
    useEffect(() => {
        fetchData('BranchApi', `/${companyId}`, token)//, token
            .then((response) => {
                if (response.responseCode == '1111') {
                    setBranches(response.response_data);
                    setTimeout(() => calculateProgress(), 200);
                }
            })
            .catch((error) => {
                console.error('Error fetching Company Details:', error);
            });
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value === 'true' ? true : e.target.type === 'checkbox' ? e.target.checked : value,
        }));

        // Calculate form completion progress
        setTimeout(() => calculateProgress(), 100);
    };

    const calculateProgress = () => {
        const requiredFields = ['Name', 'ContactNumber1', 'AddressLine1', 'Location'];
        const optionalFields = ['GSTNumber', 'ContactNumber2', 'ContactNumber3', 'Email', 'AddressLine2', 'PostalCode', 'CashOpening', 'FreightGSTRate'];

        let filledRequired = 0;
        let filledOptional = 0;

        requiredFields.forEach(field => {
            if (formData[field] && formData[field].toString().trim() !== '') {
                filledRequired++;
            }
        });

        optionalFields.forEach(field => {
            if (formData[field] && formData[field].toString().trim() !== '') {
                filledOptional++;
            }
        });

        const requiredWeight = 70;
        const optionalWeight = 30;

        const progress = (filledRequired / requiredFields.length) * requiredWeight +
            (filledOptional / optionalFields.length) * optionalWeight;

        setFormProgress(Math.round(progress));
    };


    const handleSubmit = event => {
        event.preventDefault();
        setIsLoading(true);

        setFormData(prevData => ({
            ...prevData,
            UpdatedBy: userId,
        }));

        updateData('CompanyApi', `${companyId}`, formData, token)//, token
            .then((response) => {
                console.log('Record updated successfully:', response.data);
                window.location.reload();
            })
            .catch((error) => {
                console.error('Error updating record:', error);
                setIsLoading(false);
            });
    };

    const handleOpenBranchModal = () => {
        setOpenBranchModal(true);
    };

    const handleCloseBranchModal = () => {
        setOpenBranchModal(false);
    };

    const handleOpenBranchEditModal = (branch) => {
        setSelectedBranchForEdit(branch);
        setOpenBranchEditModal(true);
    };

    const handleCloseBranchEditModal = () => {
        setOpenBranchEditModal(false);
        setSelectedBranchForEdit(null);
    };

    const handleSaveBranch = async (branchData) => {
        setIsSavingBranch(true);
        try {
            // Use the complete form data from the modal
            const apiData = {
                CompanyId: companyId,
                Code: branchData.Code,
                Name: branchData.Name,
                AddressLine1: branchData.AddressLine1,
                AddressLine2: branchData.AddressLine2,
                ContactNumber1: branchData.ContactNumber1,
                ContactNumber2: branchData.ContactNumber2,
                ContactNumber3: branchData.ContactNumber3,
                Email: branchData.Email,
                GSTNumber: branchData.GSTNumber,
                PanNumber: branchData.PanNumber,
                Location: branchData.Location,
                PinCode: branchData.PinCode,
                StateCodeId: branchData.StateCodeId ? parseInt(branchData.StateCodeId) : null,
                PaymentBankId: branchData.PaymentBankId ? parseInt(branchData.PaymentBankId) : null,
                BToBPrefix: branchData.BToBPrefix,
                BToCPrefix: branchData.BToCPrefix,
                IGSTPrefix: branchData.IGSTPrefix,
                IGSTCustomerPrefix: branchData.IGSTCustomerPrefix,
                CashOpeningBalance: branchData.CashOpeningBalance ? parseFloat(branchData.CashOpeningBalance) : 0,
                CashOpeningDate: branchData.CashOpeningDate || null,
                StockOpeningBalance: branchData.StockOpeningBalance ? parseFloat(branchData.StockOpeningBalance) : 0,
                StockOpeningDate: branchData.StockOpeningDate || null,
                IsEInvoicingEnabled: branchData.IsEInvoicingEnabled,
                IsEWaybillEnabled: branchData.IsEWaybillEnabled,
                EinvoicePortalUserName: branchData.EinvoicePortalUserName,
                EinvoicePortalPassword: branchData.EinvoicePortalPassword,
                myGSTcafeCustomerID: branchData.myGSTcafeCustomerID,
                myGSTcafeApiID: branchData.myGSTcafeApiID,
                myGSTcafeApiSecret: branchData.myGSTcafeApiSecret,
                CreatedBy:userId,
            };

            const response = await postData('BranchApi', '', apiData, token);
            if (response.responseCode === '1111') {
                // Add to branches list
                setBranches(prevBranches => [...prevBranches, {
                    id: response.response_data.Id || Date.now(),
                    code: apiData.Code,
                    name: apiData.Name,
                    contact: apiData.ContactNumber1,
                    status: 'Active'
                }]);
                handleCloseBranchModal();
            } else {
                console.error('Error creating branch:', response.message);
            }
        } catch (error) {
            console.error('Error creating branch:', error);
        } finally {
            setIsSavingBranch(false);
        }
    };

    const handleUpdateBranch = async (branchData) => {
        setIsSavingBranch(true);
        try {
            // Use the complete form data from the modal
            const apiData = {
                Id: branchData.Id,
                CompanyId: companyId,
                Code: branchData.Code,
                Name: branchData.Name,
                AddressLine1: branchData.AddressLine1,
                AddressLine2: branchData.AddressLine2,
                ContactNumber1: branchData.ContactNumber1,
                ContactNumber2: branchData.ContactNumber2,
                ContactNumber3: branchData.ContactNumber3,
                Email: branchData.Email,
                GSTNumber: branchData.GSTNumber,
                PanNumber: branchData.PanNumber,
                Location: branchData.Location,
                PinCode: branchData.PinCode,
                StateCodeId: branchData.StateCodeId ? parseInt(branchData.StateCodeId) : null,
                PaymentBankId: branchData.PaymentBankId ? parseInt(branchData.PaymentBankId) : null,
                BToBPrefix: branchData.BToBPrefix,
                BToCPrefix: branchData.BToCPrefix,
                IGSTPrefix: branchData.IGSTPrefix,
                IGSTCustomerPrefix: branchData.IGSTCustomerPrefix,
                CashOpeningBalance: branchData.CashOpeningBalance ? parseFloat(branchData.CashOpeningBalance) : 0,
                CashOpeningDate: branchData.CashOpeningDate || null,
                StockOpeningBalance: branchData.StockOpeningBalance ? parseFloat(branchData.StockOpeningBalance) : 0,
                StockOpeningDate: branchData.StockOpeningDate || null,
                IsEInvoicingEnabled: branchData.IsEInvoicingEnabled,
                IsEWaybillEnabled: branchData.IsEWaybillEnabled,
                EinvoicePortalUserName: branchData.EinvoicePortalUserName,
                EinvoicePortalPassword: branchData.EinvoicePortalPassword,
                myGSTcafeCustomerID: branchData.myGSTcafeCustomerID,
                myGSTcafeApiID: branchData.myGSTcafeApiID,
                myGSTcafeApiSecret: branchData.myGSTcafeApiSecret,
                ModifiedBy: userId,
                IsActive: true,
                CreatedBy: branchData.CreatedBy,
                CreatedDate: branchData.CreatedDate,
            };

            const response = await updateData('BranchApi', branchData.Id, apiData, token);
            if (response.responseCode === '1111') {
                // Update branches list
                setBranches(prevBranches => 
                    prevBranches.map(branch => 
                        branch.Id === branchData.Id 
                            ? { ...branch, ...apiData }
                            : branch
                    )
                );
                handleCloseBranchEditModal();
                // Refresh the branch list to get updated data
                fetchData('BranchApi', `/${companyId}`, token)
                    .then((response) => {
                        if (response.responseCode == '1111') {
                            setBranches(response.response_data);
                        }
                    })
                    .catch((error) => {
                        console.error('Error fetching updated branch data:', error);
                    });
            } else {
                console.error('Error updating branch:', response.message);
            }
        } catch (error) {
            console.error('Error updating branch:', error);
        } finally {
            setIsSavingBranch(false);
        }
    };

    return (
        <Fade in={mounted} timeout={800}>
            <div style={{ padding: '0 8px' }}>
                {/* Background Pattern */}
                <div style={{
                    position: 'absolute',
                    top: 15,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundSize: '400px 400px, 400px 400px, 20px 20px',
                    pointerEvents: 'none'
                }} />

                <div className="executive-card" style={{
                    maxWidth: '100%',
                    margin: '0',
                    borderRadius: '8px',
                    padding: '0',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Enhanced Progress Bar */}
                    <div style={{
                        height: '2px',
                        background: 'linear-gradient(90deg, #e5e7eb 0%, #f3f4f6 100%)',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            height: '100%',
                            width: `${formProgress}%`,
                            background: formProgress === 100
                                ? 'linear-gradient(90deg, #10b981 0%, #059669 50%, #047857 100%)'
                                : 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 50%, #1e40af 100%)',
                            transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            boxShadow: formProgress > 0 ? '0 0 20px rgba(59, 130, 246, 0.4)' : 'none'
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                animation: formProgress > 0 ? 'shimmerEffect 2s infinite' : 'none'
                            }} />
                        </div>
                    </div>

                    {/* Executive Header */}
                    <div className="executive-header slide-in-top" style={{
                        padding: '12px 8px',
                        position: 'relative',
                        zIndex: 2,
                        background: '#2c3e6e',
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div className="header-icon-box" style={{
                                    //background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                    padding: '8px',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.5)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)'
                                }}>
                                    <BusinessIcon style={{ fontSize: '20px', color: '#e2e6ebff' }} />
                                </div>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                                        <h6 style={{
                                            fontSize: '14px',
                                            fontWeight: '800',
                                            color: 'white',
                                            margin: 0,
                                            letterSpacing: '-0.02em',
                                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                            textTransform: 'uppercase'
                                        }}>
                                            Enterprise Management
                                        </h6>
                                        <Tooltip title="Advanced company configuration and branch management" arrow placement="right">
                                            <InfoIcon className="header-info-icon" style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.8)', cursor: 'pointer' }} />
                                        </Tooltip>
                                    </div>
                                    {/* <div className="header-description" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <p style={{
                                            margin: 0,
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            fontSize: '13px',
                                            fontWeight: '500'
                                        }}>
                                            Comprehensive Business Configuration Portal
                                        </p>
                                        <Chip
                                            icon={<CheckCircleIcon style={{ fontSize: '12px' }} />}
                                            label={`${formProgress}% Complete`}
                                            style={{
                                                height: '24px',
                                                fontSize: '11px',
                                                fontWeight: '700',
                                                background: formProgress === 100
                                                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                                    : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                                color: 'white',
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                                border: '1px solid rgba(255, 255, 255, 0.2)'
                                            }}
                                        />
                                    </div> */}
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <Tooltip title={formProgress < 100 ? "Complete configuration to save" : "Save enterprise settings"} arrow>
                                    <span>
                                        <Button
                                            variant="contained"
                                            onClick={handleSubmit}
                                            disabled={isLoading}
                                            startIcon={isLoading ? null : <SaveIcon />}
                                            className="premium-button"
                                            style={{
                                                background: isLoading ? 'rgba(255, 255, 255, 0.3)' : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                                color: isLoading ? 'rgba(255, 255, 255, 0.7)' : '#1e293b',
                                                padding: '8px 16px',
                                                borderRadius: '8px',
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                boxShadow: isLoading ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.5)',
                                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                                minHeight: '36px',
                                                textTransform: 'none',
                                                letterSpacing: '0.025em'
                                            }}
                                        >
                                            {isLoading ? <CircularProgress size={16} color="inherit" /> : <span className="btn-text">Save</span>}
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Return to dashboard" arrow>
                                    <Button
                                        variant="outlined"
                                        component={Link}
                                        to="/Home"
                                        startIcon={<ArrowBackIcon />}
                                        className="premium-button"
                                        style={{
                                            borderColor: 'rgba(255, 255, 255, 0.3)',
                                            color: 'white',
                                            padding: '8px 16px',
                                            borderRadius: '8px',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            borderWidth: '1px',
                                            minHeight: '36px',
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            backdropFilter: 'blur(10px)',
                                            textTransform: 'none'
                                        }}
                                    >
                                        <span className="btn-text">Dashboard</span>
                                    </Button>
                                </Tooltip>
                            </div>
                        </div>
                    </div>

                    <style>{`
                    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
                    
                    * {
                        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    }
                    
                    @keyframes slideInFromTop {
                        from { opacity: 0; transform: translateY(-30px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    
                    @keyframes slideInFromLeft {
                        from { opacity: 0; transform: translateX(-30px); }
                        to { opacity: 1; transform: translateX(0); }
                    }
                    
                    @keyframes slideInFromRight {
                        from { opacity: 0; transform: translateX(30px); }
                        to { opacity: 1; transform: translateX(0); }
                    }
                    
                    @keyframes fadeInScale {
                        from { opacity: 0; transform: scale(0.95); }
                        to { opacity: 1; transform: scale(1); }
                    }
                    
                    .premium-input {
                        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                        border: 1.5px solid #e5e7eb;
                        background: #ffffff;
                        position: relative;
                    }
                    
                    .premium-input:focus {
                        border-color: #3b82f6;
                        box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05);
                        transform: translateY(-1px);
                        background: #fefefe;
                    }
                    
                    .premium-input:hover:not(:focus) {
                        border-color: #9ca3af;
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
                    }
                    
                    .executive-card {
                        background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
                        border: 1px solid rgba(226, 232, 240, 0.8);
                        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
                        backdrop-filter: blur(10px);
                        animation: fadeInScale 0.6s ease-out forwards;
                    }
                    
                    .executive-header {
                        background: linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%);
                        position: relative;
                        overflow: hidden;
                    }
                    
                    .executive-header::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.05) 50%, transparent 70%);
                        animation: shimmerEffect 3s infinite;
                    }
                    
                    @keyframes shimmerEffect {
                        0% { transform: translateX(-100%); }
                        100% { transform: translateX(100%); }
                    }
                    
                    .premium-button {
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                        position: relative;
                        overflow: hidden;
                        font-weight: 600;
                        letter-spacing: 0.025em;
                    }
                    
                    .premium-button:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.1);
                    }
                    
                    .premium-button::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: -100%;
                        width: 100%;
                        height: 100%;
                        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
                        transition: left 0.5s;
                    }
                    
                    .premium-button:hover::before {
                        left: 100%;
                    }
                    
                    .data-table {
                        background: #ffffff;
                        border-radius: 12px;
                        overflow: hidden;
                        box-shadow: 0 2px 16px rgba(0, 0, 0, 0.04);
                        border: 1px solid #f1f5f9;
                    }
                    
                    .table-row {
                        transition: all 0.2s ease;
                        border-bottom: 1px solid #f8fafc;
                    }
                    
                    .table-row:hover {
                        background: linear-gradient(90deg, #eff6ff 0%, #f8fafc 100%) !important;
                        transform: translateX(2px);
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
                    }
                    
                    .table-row:hover .expand-icon {
                        color: #3b82f6 !important;
                    }
                    
                    .status-badge {
                        display: inline-flex;
                        align-items: center;
                        padding: 4px 12px;
                        border-radius: 20px;
                        font-size: 11px;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                        color: white;
                        box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
                    }
                    
                    .glass-effect {
                        background: rgba(255, 255, 255, 0.95);
                        backdrop-filter: blur(20px);
                        border: 1px solid rgba(255, 255, 255, 0.2);
                    }
                    
                    .slide-in-top { animation: slideInFromTop 0.6s ease-out; }
                    .slide-in-left { animation: slideInFromLeft 0.8s ease-out; }
                    .slide-in-right { animation: slideInFromRight 0.8s ease-out; }
                    
                    /* Mobile Responsive Styles */
                    @media (max-width: 768px) {
                        .executive-header {
                            padding: 10px 8px !important;
                        }
                        .executive-header h6 {
                            font-size: 12px !important;
                        }
                        .executive-header p {
                            font-size: 11px !important;
                        }
                        .premium-button {
                            padding: 6px 10px !important;
                            font-size: 11px !important;
                            min-height: 32px !important;
                        }
                        .premium-button .MuiButton-startIcon {
                            margin-right: 4px !important;
                        }
                        .premium-button .MuiButton-startIcon svg {
                            font-size: 14px !important;
                        }
                    }
                    
                    @media (max-width: 480px) {
                        .premium-button {
                            padding: 8px !important;
                            min-width: 36px !important;
                        }
                        .premium-button .MuiButton-startIcon {
                            margin: 0 !important;
                        }
                        .btn-text {
                            display: none !important;
                        }
                        .header-icon-box {
                            display: none !important;
                        }
                        .header-description {
                            display: none !important;
                        }
                        .header-info-icon {
                            display: none !important;
                        }
                    }
                `}</style>

                    <div style={{
                        maxWidth: '100%',
                        margin: '0',
                        background: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        padding: '8px',
                        position: 'relative'
                    }}>
                        {/* Progress Bar */}
                        <LinearProgress
                            variant="determinate"
                            value={formProgress}
                            style={{
                                height: '2px',
                                borderRadius: '4px 4px 0 0',
                                background: '#e0e0e0',
                                marginBottom: '8px'
                            }}
                            sx={{
                                '& .MuiLinearProgress-bar': {
                                    background: formProgress === 100
                                        ? 'linear-gradient(90deg, #11998e 0%, #38ef7d 100%)'
                                        : 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                                    transition: 'all 0.5s ease'
                                }
                            }}
                        />

                        {/* Compact Two Column Layout */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                            gap: '12px',
                            padding: '12px 8px 16px'
                        }}>
                            {/* Left Column - Company Configuration */}
                            <div className="slide-in-left">
                                <div className="executive-header" style={{
                                    padding: '10px 8px',
                                    borderRadius: '6px 6px 0 0',
                                    marginBottom: '0',
                                    background: '#2c3e6e',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <BusinessIcon style={{ fontSize: '18px', color: 'white' }} />
                                        <h6 style={{
                                            margin: 0,
                                            fontSize: '12.5px',
                                            fontWeight: '700',
                                            color: 'white',
                                            letterSpacing: '-0.01em',
                                            textTransform: 'uppercase'
                                        }}>
                                            Company Configuration
                                        </h6>
                                    </div>
                                    <p style={{
                                        margin: '4px 0 0 0',
                                        color: 'rgba(255, 255, 255, 0.8)',
                                        fontSize: '12px',
                                        fontWeight: '400'
                                    }}>
                                        Core business settings
                                    </p>
                                </div>
                                <div className="executive-card" style={{
                                    padding: '8px',
                                    borderRadius: '0 0 6px 6px',
                                    border: '1px solid #e2e8f0',
                                    borderTop: 'none',
                                    minHeight: '200px'
                                }}>
                                    {/* Company Name */}
                                    <div style={{ marginBottom: '12px' }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '13px',
                                            fontWeight: '700',
                                            color: '#1e293b',
                                            marginBottom: '6px',
                                            letterSpacing: '-0.01em'
                                        }}>
                                            Company Name
                                        </label>
                                        <input
                                            className="premium-input"
                                            name='Name'
                                            value={formData.Name}
                                            onChange={handleChange}
                                            type="text"
                                            placeholder="Enter your company name"
                                            required
                                            maxLength={50}
                                            style={{
                                                width: '100%',
                                                padding: '8px 8px',
                                                fontSize: '13px',
                                                borderRadius: '6px',
                                                outline: 'none',
                                                fontWeight: '500',
                                                color: '#1e293b',
                                                letterSpacing: '-0.01em'
                                            }}
                                        />
                                    </div>

                                    {/* Sales Calculation Type */}
                                    <div style={{ marginBottom: '12px' }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '13px',
                                            fontWeight: '700',
                                            color: '#1e293b',
                                            marginBottom: '6px',
                                            letterSpacing: '-0.01em'
                                        }}>
                                            Sales Calculation Type
                                        </label>
                                        <FormControl fullWidth>
                                            <Select
                                                name="SalesCalculationType"
                                                value={formData.SalesCalculationType || 1}
                                                onChange={handleChange}
                                                className="premium-input"
                                                style={{
                                                    background: 'white',
                                                    fontSize: '13px',
                                                    height: '36px',
                                                    borderRadius: '6px',
                                                    fontWeight: '500',
                                                    color: '#1e293b'
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: {
                                                            borderRadius: '8px',
                                                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
                                                            border: '1px solid #e2e8f0',
                                                            marginTop: '2px'
                                                        }
                                                    }
                                                }}
                                            >
                                                <MenuItem value={1} style={{ fontSize: '14px', fontWeight: '500', padding: '8px 12px' }}>
                                                    1. For Inclusive VAT
                                                </MenuItem>
                                                <MenuItem value={2} style={{ fontSize: '14px', fontWeight: '500', padding: '8px 12px' }}>
                                                    2. For Exclusive VAT
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </div>

                                </div>
                            </div>

                            {/* Right Column - Branch Management */}
                            <div className="slide-in-right">
                                <div className="executive-header" style={{
                                    padding: '10px 8px',
                                    borderRadius: '6px 6px 0 0',
                                    marginBottom: '0',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    background: '#2c3e6e',
                                }}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <AccountTreeIcon style={{ fontSize: '18px', color: 'white' }} />
                                            <h2 style={{
                                                margin: 0,
                                                fontSize: '13px',
                                                fontWeight: '700',
                                                color: 'white',
                                                letterSpacing: '-0.01em',
                                                textTransform: 'uppercase'
                                            }}>
                                                Branch Network
                                            </h2>
                                        </div>
                                        <p style={{
                                            margin: '4px 0 0 0',
                                            color: 'rgba(255, 255, 255, 0.8)',
                                            fontSize: '12px',
                                            fontWeight: '400'
                                        }}>
                                            Manage locations
                                        </p>
                                    </div>
                                </div>
                                <div className="executive-card" style={{
                                    padding: '8px',
                                    borderRadius: '0 0 6px 6px',
                                    border: '1px solid #e2e8f0',
                                    borderTop: 'none',
                                    minHeight: '200px'
                                }}>
                                    {/* Add New Branch Button */}
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        className="premium-button"
                                        onClick={handleOpenBranchModal}
                                        style={{
                                            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                            color: 'white',
                                            marginBottom: '10px',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            fontWeight: '700',
                                            padding: '6px 10px',
                                            textTransform: 'none',
                                            letterSpacing: '0.025em',
                                            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                                            height: '28px'
                                        }}
                                    >
                                        Add New Branch
                                    </Button>

                                    {/* Compact Branch Table */}
                                    <div className="data-table">
                                        {/* Table Header */}
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: '30px 40px 60px 1fr 100px 60px 40px',
                                            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                            padding: '6px 8px',
                                            fontSize: '10px',
                                            fontWeight: '800',
                                            color: '#475569',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            borderBottom: '1px solid #e2e8f0'
                                        }}>
                                            <div></div>
                                            <div>ID</div>
                                            <div>Code</div>
                                            <div>Name</div>
                                            <div>Contact</div>
                                            <div>Status</div>
                                            <div>Edit</div>
                                        </div>

                                        {/* Table Rows */}
                                        {branches.map((branch, index) => (
                                            <div key={branch.id}>
                                                <div
                                                    className="table-row"
                                                    onClick={() => handleBranchRowClick(branch.Id)}
                                                    style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '30px 40px 60px 1fr 100px 60px 40px',
                                                        padding: '8px 8px',
                                                        fontSize: '11px',
                                                        fontWeight: '500',
                                                        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
                                                        color: '#fbbf24',
                                                        alignItems: 'center',
                                                        cursor: 'pointer',
                                                        borderLeft: expandedBranchId === branch.Id ? '3px solid #fbbf24' : '3px solid transparent',
                                                        transition: 'none'
                                                    }}
                                                >
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        {expandedBranchId === branch.Id ? (
                                                            <ExpandLessIcon style={{
                                                                fontSize: '18px',
                                                                color: '#fbbf24',
                                                                transition: 'none'
                                                            }} />
                                                        ) : (
                                                            <ExpandMoreIcon style={{
                                                                fontSize: '18px',
                                                                color: '#fbbf24',
                                                                transition: 'none'
                                                            }} />
                                                        )}
                                                    </div>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        fontWeight: '700'
                                                    }}>
                                                        {index === 0 && expandedBranchId !== branch.Id && (
                                                            <div style={{
                                                                width: '4px',
                                                                height: '4px',
                                                                borderRadius: '50%',
                                                                background: '#10b981',
                                                                boxShadow: '0 0 6px rgba(16, 185, 129, 0.6)'
                                                            }} />
                                                        )}
                                                        {branch.Id}
                                                    </div>
                                                    <div style={{
                                                        fontFamily: 'monospace',
                                                        fontWeight: '700',
                                                        fontSize: '11px'
                                                    }}>
                                                        {branch.Code}
                                                    </div>
                                                    <div style={{ fontWeight: '600', fontSize: '12px' }}>{branch.Name}</div>
                                                    <div style={{
                                                        fontFamily: 'monospace',
                                                        fontSize: '11px',
                                                        //color: index === 0 && expandedBranchId !== branch.Id ? 'rgba(255, 255, 255, 0.8)' : '#6b7280'
                                                    }}>
                                                        {branch.ContactNumber1}
                                                    </div>
                                                    <div>
                                                        <span className="status-badge" style={{
                                                            background: index === 0 && expandedBranchId !== branch.Id
                                                                ? 'rgba(16, 185, 129, 0.2)'
                                                                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                            color: index === 0 && expandedBranchId !== branch.Id ? '#10b981' : 'white',
                                                            border: index === 0 && expandedBranchId !== branch.Id ? '1px solid rgba(16, 185, 129, 0.3)' : 'none',
                                                            padding: '2px 6px',
                                                            fontSize: '9px'
                                                        }}>
                                                            {branch.IsActive ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </div>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenBranchEditModal(branch);
                                                            }}
                                                            style={{
                                                                color: '#fbbf24',
                                                                padding: '4px',
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                            sx={{
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(251, 191, 36, 0.1)',
                                                                    transform: 'scale(1.1)'
                                                                }
                                                            }}
                                                        >
                                                            <EditIcon style={{ fontSize: '14px' }} />
                                                        </IconButton>
                                                    </div>
                                                </div>
                                                {/* Collapsible Detail Card */}
                                                <Collapse in={expandedBranchId === branch.Id} timeout={300}>
                                                    <BranchDetailCard branch={branch} />
                                                </Collapse>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Compact Pagination */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: '8px',
                                        marginTop: '12px',
                                        padding: '8px',
                                        background: '#f8fafc',
                                        borderRadius: '6px',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            disabled
                                            style={{
                                                minWidth: '28px',
                                                height: '28px',
                                                borderRadius: '6px',
                                                borderColor: '#e5e7eb',
                                                color: '#9ca3af',
                                                fontWeight: '600',
                                                fontSize: '12px'
                                            }}
                                        >
                                            ‹
                                        </Button>
                                        <div style={{
                                            padding: '4px 8px',
                                            background: 'white',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            color: '#374151',
                                            border: '1px solid #e2e8f0'
                                        }}>
                                            1 of 1
                                        </div>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            disabled
                                            style={{
                                                minWidth: '28px',
                                                height: '28px',
                                                borderRadius: '6px',
                                                borderColor: '#e5e7eb',
                                                color: '#9ca3af',
                                                fontWeight: '600',
                                                fontSize: '12px'
                                            }}
                                        >
                                            ›
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Compact Footer */}
                <div className="glass-effect" style={{
                    position: 'fixed',
                    bottom: '12px',
                    right: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.05)',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: '700',
                    color: '#1e293b',
                    fontSize: '11px',
                    zIndex: 1000,
                    letterSpacing: '0.025em'
                }}>
                    <div style={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        padding: '4px',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 6px rgba(59, 130, 246, 0.3)'
                    }}>
                        <CopyrightIcon style={{ fontSize: '14px', color: 'white' }} />
                    </div>
                    <span>EFFTABS SOLUTIONS LLP</span>
                </div>

                {/* New Branch Modal - Simplified Design */}
                <BranchCreateModal
                    open={openBranchModal}
                    onClose={handleCloseBranchModal}
                    onSave={handleSaveBranch}
                    isSaving={isSavingBranch}
                />

                {/* Edit Branch Modal */}
                <BranchEditModal
                    open={openBranchEditModal}
                    onClose={handleCloseBranchEditModal}
                    onSave={handleUpdateBranch}
                    isSaving={isSavingBranch}
                    branchData={selectedBranchForEdit}
                />
            </div>
        </Fade>
    );
};

export default CompanyEdit;
